import {
  createEmptyOnboardingPayload,
  type OnboardingPayload,
} from '../../../types/onboardingIntake';
import type { StepItem } from '../../../ui/v2';
import { phoneExtensionError } from '../../../utils/phoneExtension';

/**
 * Store onboarding intake — pure logic. The payload is `OnboardingPayload`
 * verbatim, so a submit is the same JSON `POST /api/public/onboarding-
 * intakes/` has always accepted. The page and the step forms call this; the
 * admin intake surfaces can read `summarize()` and the step vocabulary too.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isEmail = (v: string) => EMAIL_RE.test(v.trim());

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

export const SERVICES = [
  'Phone orders',
  'Reservations',
  'Events',
  'Catering',
] as const;
export type Service = (typeof SERVICES)[number];

/** Service names older drafts may still hold. */
const LEGACY_SERVICE_NAMES: Record<string, Service> = {
  'Online ordering': 'Phone orders',
};

/**
 * Bring a saved draft's services in line with today's checkboxes: legacy
 * names renamed, retired ones (Guest FAQs — answering questions is always
 * on) dropped, duplicates collapsed.
 */
export function normalizeDraft(d: IntakeDraft): IntakeDraft {
  const used = d.payload.business.services_used;
  const current = used
    .map((s) => LEGACY_SERVICE_NAMES[s] ?? s)
    .filter((s): s is Service => (SERVICES as readonly string[]).includes(s));
  const deduped = current.filter((s, i) => current.indexOf(s) === i);
  if (
    deduped.length === used.length &&
    deduped.every((s, i) => s === used[i])
  ) {
    return d;
  }
  return {
    ...d,
    payload: {
      ...d.payload,
      business: {
        ...d.payload.business,
        services_used: deduped,
      },
    },
  };
}

export const ORDER_CUTOFFS = [0, 10, 15, 30, 45, 60] as const;

export const HUMAN_TRANSFER_DISABLED =
  'Human transfers are not enabled, based on your earlier answer.';

export const humanTransfersDisabled = (p: OnboardingPayload) =>
  p.call_handling.human_transfer_enabled === false;

/** Drop transfer choices that the earlier answer no longer allows. */
export function clearDisabledTransfers(
  p: OnboardingPayload,
): OnboardingPayload {
  if (!humanTransfersDisabled(p)) return p;
  const reservations = { ...p.reservations };
  if (reservations.handling === 'transfer') reservations.handling = undefined;
  if (reservations.late_handling === 'transfer') {
    reservations.late_handling = undefined;
  }
  if (reservations.cancellation_failure_handling === 'transfer') {
    reservations.cancellation_failure_handling = undefined;
  }
  return {
    ...p,
    call_handling: {
      ...p.call_handling,
      transfer_phone_number: undefined,
      transfer_phone_number_ext: undefined,
    },
    ordering:
      p.ordering.phone_order_handling === 'transfer_to_store'
        ? { ...p.ordering, phone_order_handling: undefined }
        : p.ordering,
    reservations,
    events:
      p.events.handling === 'transfer'
        ? { ...p.events, handling: 'decline' }
        : p.events,
    catering:
      p.catering.handling === 'transfer'
        ? { ...p.catering, handling: 'decline' }
        : p.catering,
  };
}

/** Phone orders are taken, so a missing cutoff is "right at closing" (0). */
export function closingCutoffMinutes(
  ordering: OnboardingPayload['ordering'],
): number | undefined {
  const handling = ordering.phone_order_handling;
  if (!handling || handling === 'do_not_accept') {
    return ordering.last_order_offset_minutes;
  }
  return ordering.last_order_offset_minutes ?? 0;
}

export type StepKey =
  | 'restaurant'
  | 'people'
  | 'services'
  | 'orders'
  | 'reservations'
  | 'events'
  | 'faqs'
  | 'review';

const STEP_META: Record<StepKey, StepItem<StepKey>> = {
  restaurant: {
    key: 'restaurant',
    label: 'Your restaurant',
    description: 'Names, addresses and how to reach the store.',
  },
  people: {
    key: 'people',
    label: 'Who we work with',
    description: 'Your day-to-day contact and who signs.',
  },
  services: {
    key: 'services',
    label: 'What Arthur handles',
    description:
      'Pick the calls Arthur should take. The next steps follow from this.',
  },
  orders: {
    key: 'orders',
    label: 'Phone orders',
    description: 'How Arthur takes, links or transfers orders.',
  },
  reservations: {
    key: 'reservations',
    label: 'Reservations',
    description: 'Booking, party sizes, late guests.',
  },
  events: {
    key: 'events',
    label: 'Events & catering',
    description: 'Where event and catering inquiries go.',
  },
  faqs: {
    key: 'faqs',
    label: 'Guest questions',
    description: 'Gift cards, parking, your FAQ page and documents for Arthur.',
  },
  review: {
    key: 'review',
    label: 'Review',
    description: 'A last look before you submit.',
  },
};

export const hasService = (p: OnboardingPayload, s: Service) =>
  p.business.services_used.includes(s);

const hasEventsOrCatering = (p: OnboardingPayload) =>
  hasService(p, 'Events') || hasService(p, 'Catering');

/** Steps that apply to this restaurant, in order. Conditional steps drop out with the service. */
export function stepsFor(p: OnboardingPayload): StepItem<StepKey>[] {
  const keys: StepKey[] = ['restaurant', 'people', 'services'];
  if (hasService(p, 'Phone orders')) keys.push('orders');
  if (hasService(p, 'Reservations')) keys.push('reservations');
  if (hasEventsOrCatering(p)) keys.push('events');
  keys.push('faqs', 'review');
  return keys.map((k) => STEP_META[k]);
}

// ---------------------------------------------------------------------------
// Draft
// ---------------------------------------------------------------------------

export interface AddressParts {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  timezone?: string;
}

/** An address the user picked from the lookup: what it reads as, and its parts. */
export interface PickedAddress {
  label: string;
  parts: AddressParts;
}

export interface IntakeDraft {
  payload: OnboardingPayload;
  /** Structured addresses only exist once the user picked a suggestion. */
  legalAddressSelected: boolean;
  restaurantAddressSelected: boolean;
  sameRepresentative: boolean;
  step: StepKey;
  /** Steps the user has completed at least once (StepNav and Review). */
  visited: StepKey[];
  /** Set once Review has been opened, so Continue can return there after a refresh. */
  reviewed: boolean;
  /** "Other" was picked for the order cutoff, so the minutes are typed in. */
  customOrderCutoff?: boolean;
  updatedAt: number | null;
}

export const emptyDraft = (): IntakeDraft => ({
  payload: createEmptyOnboardingPayload(),
  legalAddressSelected: false,
  restaurantAddressSelected: false,
  sameRepresentative: false,
  step: 'restaurant',
  visited: [],
  reviewed: false,
  updatedAt: null,
});

export const applyLegalAddress = (
  p: OnboardingPayload,
  a: PickedAddress,
): OnboardingPayload => ({
  ...p,
  location: {
    ...p.location,
    legal_business_address_raw: a.label,
    legal_address_line_1: a.parts.address_line_1,
    legal_address_line_2: a.parts.address_line_2,
    legal_city: a.parts.city,
    legal_state: a.parts.state,
    legal_postal_code: a.parts.postal_code,
    legal_country: a.parts.country || p.location.legal_country,
  },
});

export const applyRestaurantAddress = (
  p: OnboardingPayload,
  a: PickedAddress,
): OnboardingPayload => ({
  ...p,
  location: {
    ...p.location,
    restaurant_address_raw: a.label,
    address_line_1: a.parts.address_line_1,
    address_line_2: a.parts.address_line_2,
    city: a.parts.city,
    state: a.parts.state,
    postal_code: a.parts.postal_code,
    country: a.parts.country || p.location.country,
    timezone: a.parts.timezone || p.location.timezone,
  },
  registration: a.parts.country
    ? {
        ...p.registration,
        country: p.registration.country || a.parts.country,
        issuing_country: p.registration.issuing_country || a.parts.country,
      }
    : p.registration,
});

/** Typing after a pick clears the structured parts so a half-edited address can't be submitted. */
export const clearLegalAddress = (
  p: OnboardingPayload,
  raw: string,
): OnboardingPayload => ({
  ...p,
  location: {
    ...p.location,
    legal_business_address_raw: raw,
    legal_address_line_1: '',
    legal_address_line_2: '',
    legal_city: '',
    legal_state: '',
    legal_postal_code: '',
  },
});

export const clearRestaurantAddress = (
  p: OnboardingPayload,
  raw: string,
): OnboardingPayload => ({
  ...p,
  location: {
    ...p.location,
    restaurant_address_raw: raw,
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    timezone: '',
  },
});

const hasStructuredLegal = (p: OnboardingPayload) =>
  Boolean(
    p.location.legal_address_line_1 &&
      p.location.legal_city &&
      p.location.legal_state &&
      p.location.legal_postal_code &&
      p.location.legal_country,
  );

const hasStructuredRestaurant = (p: OnboardingPayload) =>
  Boolean(
    p.location.address_line_1 &&
      p.location.city &&
      p.location.state &&
      p.location.postal_code &&
      p.location.country,
  );

export const representativeFromContact = (
  c: OnboardingPayload['primary_contact'],
): OnboardingPayload['authorized_representative'] => ({
  name: c.name,
  phone: c.phone,
  email: c.email,
  business_title: c.title_role,
  job_position: c.title_role,
});

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** Keyed by a dotted path the step forms use for `Field error` and focus. */
export type FieldErrors = Partial<Record<string, string>>;

const blank = (v: unknown) =>
  v == null || (typeof v === 'string' && v.trim() === '');
const phoneOk = (v: string) => v.replace(/\D/g, '').length >= 10;
// An EIN (12-3456789) or a Canadian Business Number: 9 digits, optionally
// followed by a program account like RC0001. Separators are ignored.
const taxIdOk = (v: string) =>
  /^\d{9}([A-Z]{2}\d{4})?$/.test(v.replace(/[\s.-]/g, '').toUpperCase());
const urlOk = (v: string) =>
  /^(https?:\/\/)?[\w-]+(\.[\w-]+)+([/?#].*)?$/i.test(v.trim());

const PICK_ADDRESS =
  'Pick the address from the suggestions so we get street, city and postal code.';
const INCOMPLETE_ADDRESS =
  'That address is missing a street, city, state or postal code. Pick a more complete suggestion.';
const PHONE = 'Enter a phone number with area code.';
const EMAIL = 'Enter an email like name@restaurant.com.';
const TAX_ID =
  'Enter an EIN like 12-3456789, or a 9-digit Canadian Business Number.';

export function validateStep(step: StepKey, d: IntakeDraft): FieldErrors {
  const p = d.payload;
  const e: FieldErrors = {};
  const req = (key: string, v: unknown, msg = 'Required.') => {
    if (blank(v)) e[key] = msg;
  };
  const phone = (key: string, v: string | undefined, required = true) => {
    if (required) req(key, v);
    if (!blank(v) && !phoneOk(v ?? '')) e[key] = PHONE;
  };
  const extension = (key: string, v: string | undefined) => {
    const message = phoneExtensionError(v);
    if (message) e[key] = message;
  };
  const email = (key: string, v: string | undefined, required = true) => {
    if (required) req(key, v);
    if (!blank(v) && !isEmail(v ?? '')) e[key] = EMAIL;
  };
  const url = (key: string, v: string | undefined, msg: string) => {
    req(key, v, msg);
    if (!blank(v) && !urlOk(v ?? ''))
      e[key] = 'Enter a web address like example.com.';
  };
  switch (step) {
    case 'restaurant': {
      req('business.restaurant_name', p.business.restaurant_name);
      req('business.legal_business_name', p.business.legal_business_name);
      req('registration.tax_id', p.registration.tax_id);
      if (
        !blank(p.registration.tax_id) &&
        !taxIdOk(p.registration.tax_id ?? '')
      )
        e['registration.tax_id'] = TAX_ID;
      if (blank(p.location.legal_business_address_raw))
        e['location.legal'] = 'Required.';
      else if (!d.legalAddressSelected) e['location.legal'] = PICK_ADDRESS;
      else if (!hasStructuredLegal(p)) e['location.legal'] = INCOMPLETE_ADDRESS;
      if (blank(p.location.restaurant_address_raw))
        e['location.restaurant'] = 'Required.';
      else if (!d.restaurantAddressSelected)
        e['location.restaurant'] = PICK_ADDRESS;
      else if (!hasStructuredRestaurant(p))
        e['location.restaurant'] = INCOMPLETE_ADDRESS;
      phone('location.phone', p.location.phone);
      url('business.website', p.business.website, 'Required.');
      break;
    }
    case 'people': {
      req('primary_contact.name', p.primary_contact.name);
      req('primary_contact.title_role', p.primary_contact.title_role);
      phone('primary_contact.phone', p.primary_contact.phone);
      email('primary_contact.email', p.primary_contact.email);
      if (!d.sameRepresentative) {
        const r = p.authorized_representative;
        req('authorized_representative.name', r.name);
        email('authorized_representative.email', r.email);
      }
      break;
    }
    case 'services': {
      // No picks is fine: answering guests' questions is always on.
      if (p.call_handling.human_transfer_enabled == null)
        e['call_handling.human_transfer_enabled'] = 'Choose one.';
      if (p.call_handling.human_transfer_enabled)
        phone(
          'call_handling.transfer_phone_number',
          p.call_handling.transfer_phone_number,
          false,
        );
      extension(
        'call_handling.transfer_phone_number_ext',
        p.call_handling.transfer_phone_number_ext,
      );
      break;
    }
    case 'orders': {
      const o = p.ordering;
      if (!o.phone_order_handling)
        e['ordering.phone_order_handling'] = 'Choose one.';
      if (o.phone_order_handling === 'send_order_link')
        req(
          'ordering.ordering_link',
          o.ordering_link,
          'Add the ordering link Arthur should text.',
        );
      if (o.phone_order_handling === 'transfer_to_store') {
        if (humanTransfersDisabled(p)) {
          e['ordering.phone_order_handling'] = HUMAN_TRANSFER_DISABLED;
        } else {
          // Optional: a blank number falls back to the main transfer number.
          phone(
            'ordering.order_requests_phone_number',
            o.order_requests_phone_number,
            false,
          );
          extension(
            'ordering.order_requests_phone_number_ext',
            o.order_requests_phone_number_ext,
          );
        }
      }
      if (
        d.customOrderCutoff &&
        o.phone_order_handling &&
        o.phone_order_handling !== 'do_not_accept'
      ) {
        const m = o.last_order_offset_minutes;
        if (m == null)
          e['ordering.last_order_offset_minutes'] =
            'Enter how many minutes before close.';
        else if (!Number.isInteger(m) || m < 1)
          e['ordering.last_order_offset_minutes'] =
            'Enter a whole number of at least 1.';
      }
      if (!o.delivery_handling) e['ordering.delivery_handling'] = 'Choose one.';
      if (o.delivery_handling === 'explain_third_party_delivery')
        req(
          'ordering.how_to_order_delivery_message',
          o.how_to_order_delivery_message,
          'Tell Arthur what to say.',
        );
      if (!o.payment_handling) e['ordering.payment_handling'] = 'Choose one.';
      break;
    }
    case 'reservations': {
      const r = p.reservations;
      // `transfer` came from an older draft; it is no longer an option.
      if (!r.handling || r.handling === 'transfer')
        e['reservations.handling'] = 'Choose one.';
      if (r.handling === 'send_link')
        req(
          'reservations.reservation_link',
          r.reservation_link,
          'Add the booking link Arthur should text.',
        );
      if (r.late_handling === 'transfer' && humanTransfersDisabled(p)) {
        e['reservations.late_handling'] = HUMAN_TRANSFER_DISABLED;
      }
      if (
        r.cancellation_failure_handling === 'transfer' &&
        humanTransfersDisabled(p)
      ) {
        e['reservations.cancellation_failure_handling'] =
          HUMAN_TRANSFER_DISABLED;
      }
      if (hasService(p, 'Reservations')) {
        // Only OpenTable bookings use the party limit, so only they need it.
        const party = r.max_party_size;
        if (party == null) {
          if (r.handling === 'book_directly')
            e['reservations.max_party_size'] =
              'Enter the largest party Arthur may book.';
        } else if (!Number.isInteger(party) || party < 1)
          e['reservations.max_party_size'] =
            'Enter a whole number of at least 1.';
      }
      email('reservations.reservation_email', r.reservation_email, false);
      break;
    }
    case 'events': {
      if (hasService(p, 'Events')) {
        const ev = p.events;
        // `basic_info_only` came from an older draft; it is no longer an option.
        if (
          !ev.handling ||
          ev.handling === 'decline' ||
          ev.handling === 'basic_info_only'
        )
          e['events.handling'] = 'Choose one.';
        if (ev.handling === 'text_link')
          req(
            'events.event_link',
            ev.event_link,
            'Add the link Arthur should text.',
          );
        if (ev.handling === 'transfer') {
          if (humanTransfersDisabled(p)) {
            e['events.handling'] = HUMAN_TRANSFER_DISABLED;
          } else {
            req(
              'events.event_forwarding_number',
              ev.event_forwarding_number,
              'Add the number Arthur should transfer to.',
            );
            phone(
              'events.event_forwarding_number',
              ev.event_forwarding_number,
              false,
            );
            extension(
              'events.event_forwarding_number_ext',
              ev.event_forwarding_number_ext,
            );
          }
        }
      }
      if (hasService(p, 'Catering')) {
        const c = p.catering;
        if (!c.handling || c.handling === 'decline')
          e['catering.handling'] = 'Choose one.';
        if (c.handling === 'transfer') {
          if (humanTransfersDisabled(p)) {
            e['catering.handling'] = HUMAN_TRANSFER_DISABLED;
          } else {
            req(
              'catering.catering_forwarding_number',
              c.catering_forwarding_number,
              'Add the number Arthur should transfer to.',
            );
            phone(
              'catering.catering_forwarding_number',
              c.catering_forwarding_number,
              false,
            );
            extension(
              'catering.catering_forwarding_number_ext',
              c.catering_forwarding_number_ext,
            );
          }
        }
        if (c.handling === 'provide_contact')
          url(
            'catering.catering_link',
            c.catering_link,
            'Add the catering link Arthur should text.',
          );
      }
      break;
    }
    case 'faqs': {
      if (p.gift_cards.handling === 'send_link')
        req(
          'gift_cards.gift_card_link',
          p.gift_cards.gift_card_link,
          'Add the link Arthur should text.',
        );
      break;
    }
    case 'review':
      break;
  }
  return e;
}

export const errorCount = (e: FieldErrors) =>
  Object.values(e).filter(Boolean).length;

/** The first step (in order) with an error, and its errors. */
export function firstInvalidStep(
  d: IntakeDraft,
): { step: StepKey; errors: FieldErrors } | null {
  for (const s of stepsFor(d.payload)) {
    const errors = validateStep(s.key, d);
    if (errorCount(errors) > 0) return { step: s.key, errors };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Submit
// ---------------------------------------------------------------------------

/**
 * The payload to send. Sections for services the restaurant didn't pick
 * are hidden in the intake, so whatever they hold (from picking a service
 * and then unpicking it) is reset rather than sent as routing nobody can
 * see.
 */
export function payloadForSubmit(d: IntakeDraft): OnboardingPayload {
  const p = d.payload;
  const empty = createEmptyOnboardingPayload();
  return {
    ...p,
    registration: {
      ...p.registration,
      // "12-3456789" and "123456789 RC0001" are stored as digits (plus a
      // CBN's program account): downstream systems reject the separators.
      tax_id: p.registration.tax_id.replace(/[\s.-]/g, '').toUpperCase(),
    },
    authorized_representative: d.sameRepresentative
      ? representativeFromContact(p.primary_contact)
      : p.authorized_representative,
    call_handling: {
      ...p.call_handling,
      transfer_phone_number: p.call_handling.human_transfer_enabled
        ? p.call_handling.transfer_phone_number
        : undefined,
      transfer_phone_number_ext: p.call_handling.human_transfer_enabled
        ? p.call_handling.transfer_phone_number_ext
        : undefined,
    },
    ordering: hasService(p, 'Phone orders')
      ? {
          ...p.ordering,
          last_order_offset_minutes: closingCutoffMinutes(p.ordering),
        }
      : empty.ordering,
    reservations: hasService(p, 'Reservations')
      ? p.reservations
      : empty.reservations,
    events: hasService(p, 'Events')
      ? {
          ...p.events,
          event_inquiry_questions: (p.events.event_inquiry_questions ?? [])
            .map((q) => q.trim())
            .filter(Boolean),
        }
      : empty.events,
    catering: hasService(p, 'Catering') ? p.catering : empty.catering,
  };
}

// ---------------------------------------------------------------------------
// Review summary
// ---------------------------------------------------------------------------

export interface SummaryRow {
  label: string;
  value: string | null;
}

export interface SummarySection {
  step: StepKey;
  title: string;
  rows: SummaryRow[];
}

const PHONE_ORDER_WORDS: Record<string, string> = {
  agent_takes_order: 'Arthur takes the order',
  send_order_link: 'Arthur texts the ordering link',
  transfer_to_store: 'Transfer to the store',
  do_not_accept: 'No phone orders',
};
const DELIVERY_WORDS: Record<string, string> = {
  agent_takes_delivery: 'Arthur takes delivery orders',
  explain_third_party_delivery: 'Arthur explains how to order delivery',
  pickup_only: 'Pickup only',
};
const PAYMENT_WORDS: Record<string, string> = {
  online_only: 'Pay online before the order is placed',
  pay_on_site: 'Pay at pickup',
  both: 'Either',
};
const RESERVATION_WORDS: Record<string, string> = {
  book_directly: 'Arthur books directly',
  send_link: 'Arthur texts a booking link',
  transfer: 'Transfer to staff',
  decline: 'No reservations',
};
const EVENT_WORDS: Record<string, string> = {
  text_link: 'Arthur texts an events link',
  transfer: 'Transfer to the events team',
  collect_lead: 'Arthur collects the details',
  basic_info_only: 'Basics and contact info only',
  decline: 'Not handled',
};
const CATERING_WORDS: Record<string, string> = {
  transfer: 'Transfer to a catering contact',
  provide_contact: 'Arthur says how to reach catering',
  collect_lead: 'Arthur handles the catering',
  decline: 'Not handled',
};
const GIFT_WORDS: Record<string, string> = {
  send_link: 'Arthur texts a link',
  explain_only: 'Arthur explains where to buy',
  unavailable: 'No gift cards',
};

const word = (map: Record<string, string>, v?: string) =>
  v ? (map[v] ?? v) : null;
const list = (a?: string[]) => (a && a.length ? a.join(', ') : null);
const text = (v?: string | number | null) =>
  v == null || v === '' ? null : String(v);
const joined = (...parts: (string | undefined)[]) =>
  parts.filter(Boolean).join(' · ') || null;

export function summarize(d: IntakeDraft): SummarySection[] {
  const p = d.payload;
  const out: SummarySection[] = [
    {
      step: 'restaurant',
      title: 'Your restaurant',
      rows: [
        { label: 'Restaurant', value: text(p.business.restaurant_name) },
        { label: 'Legal name', value: text(p.business.legal_business_name) },
        { label: 'Tax ID', value: text(p.registration.tax_id) },
        {
          label: 'Legal address',
          value: text(p.location.legal_business_address_raw),
        },
        {
          label: 'Restaurant address',
          value: text(p.location.restaurant_address_raw),
        },
        { label: 'Phone', value: text(p.location.phone) },
        { label: 'Website', value: text(p.business.website) },
      ],
    },
    {
      step: 'people',
      title: 'Who we work with',
      rows: [
        {
          label: 'Primary contact',
          value: joined(p.primary_contact.name, p.primary_contact.title_role),
        },
        {
          label: 'Reach them at',
          value: joined(p.primary_contact.email, p.primary_contact.phone),
        },
        {
          label: 'Signs for the business',
          value: d.sameRepresentative
            ? 'Same person'
            : joined(
                p.authorized_representative.name,
                p.authorized_representative.business_title,
              ),
        },
      ],
    },
    {
      step: 'services',
      title: 'What Arthur handles',
      rows: [
        { label: 'Services', value: list(p.business.services_used) },
        {
          label: 'Human transfers',
          value:
            p.call_handling.human_transfer_enabled == null
              ? null
              : p.call_handling.human_transfer_enabled
                ? 'Yes'
                : 'No — text/email staff instead',
        },
        ...(p.call_handling.human_transfer_enabled
          ? [
              {
                label: 'Transfer calls to',
                value: text(p.call_handling.transfer_phone_number),
              },
            ]
          : []),
        {
          label: 'Biggest pain point',
          value: text(p.business.biggest_pain_point),
        },
      ],
    },
  ];
  if (hasService(p, 'Phone orders')) {
    const o = p.ordering;
    const cutoff = closingCutoffMinutes(o);
    out.push({
      step: 'orders',
      title: 'Phone orders',
      rows: [
        {
          label: 'Orders',
          value: word(PHONE_ORDER_WORDS, o.phone_order_handling),
        },
        { label: 'Delivery', value: word(DELIVERY_WORDS, o.delivery_handling) },
        { label: 'Payment', value: word(PAYMENT_WORDS, o.payment_handling) },
        {
          label: 'POS',
          value: text(
            o.pos_other_name ||
              (typeof o.pos_system === 'string' ? o.pos_system : ''),
          ),
        },
        {
          label: 'Last orders',
          value:
            cutoff == null
              ? null
              : cutoff === 0
                ? 'Right at closing'
                : `${cutoff} min before close`,
        },
      ],
    });
  }
  if (hasService(p, 'Reservations')) {
    const r = p.reservations;
    out.push({
      step: 'reservations',
      title: 'Reservations',
      rows: [
        { label: 'Bookings', value: word(RESERVATION_WORDS, r.handling) },
        {
          label: 'Largest party',
          value: r.max_party_size != null ? `${r.max_party_size} guests` : null,
        },
        {
          label: 'Grace period',
          value:
            r.default_grace_period_minutes != null
              ? `${r.default_grace_period_minutes} min`
              : null,
        },
      ],
    });
  }
  if (hasEventsOrCatering(p)) {
    const rows: SummaryRow[] = [];
    if (hasService(p, 'Events')) {
      rows.push({
        label: 'Events',
        value: word(EVENT_WORDS, p.events.handling),
      });
    }
    if (hasService(p, 'Catering'))
      rows.push({
        label: 'Catering',
        value: word(CATERING_WORDS, p.catering.handling),
      });
    out.push({ step: 'events', title: 'Events & catering', rows });
  }
  out.push({
    step: 'faqs',
    title: 'Guest questions',
    rows: [
      { label: 'Gift cards', value: word(GIFT_WORDS, p.gift_cards.handling) },
      { label: 'Parking', value: text(p.store_faqs.parking_info) },
      { label: 'FAQ page', value: text(p.knowledge_base.faq_url) },
    ],
  });
  return out;
}

// ---------------------------------------------------------------------------
// Activation prefill
// ---------------------------------------------------------------------------

/** What the signer typed at checkout (`GET …/onboarding-prefill/`). */
export interface ActivationPrefill {
  business_name?: string | null;
  signer_name?: string | null;
  signer_email?: string | null;
  signer_phone?: string | null;
}

const prefillText = (value: string | null | undefined) => (value ?? '').trim();

/** True when the activation response has something applyPrefill would copy. */
export function hasActivationPrefill(
  pre: ActivationPrefill | null | undefined,
): boolean {
  if (!pre) return false;
  return [
    pre.business_name,
    pre.signer_name,
    pre.signer_email,
    pre.signer_phone,
  ].some((value) => prefillText(value) !== '');
}

/** Fills only blank fields, so a draft the user already edited wins. */
export function applyPrefill(
  p: OnboardingPayload,
  pre: ActivationPrefill,
): OnboardingPayload {
  const business = prefillText(pre.business_name);
  const name = prefillText(pre.signer_name);
  const email = prefillText(pre.signer_email);
  const phone = prefillText(pre.signer_phone);
  return {
    ...p,
    business: {
      ...p.business,
      restaurant_name: p.business.restaurant_name || business,
      legal_business_name: p.business.legal_business_name || business,
    },
    primary_contact: {
      ...p.primary_contact,
      name: p.primary_contact.name || name,
      email: p.primary_contact.email || email,
      phone: p.primary_contact.phone || phone,
    },
  };
}
